import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCommunityTopics, useUniqueTags } from "@/hooks/useCommunityTopics";
import { Skeleton } from "@/components/ui/skeleton";

export const RecommendedTopics = () => {
  const [selectedCategory, setSelectedCategory] = useState('recommend');
  const navigate = useNavigate();
  const { data: topics, isLoading } = useCommunityTopics(selectedCategory);
  const { data: uniqueTags, isLoading: isLoadingTags } = useUniqueTags();

  // Build dynamic categories: Recommended, Featured, then all unique tags
  const categories = [
    { id: 'recommend', label: 'Recommended' },
    { id: 'featured', label: 'Featured' },
    ...(uniqueTags || []).map(tag => ({ id: tag, label: tag })),
  ];

  const filteredTopics = selectedCategory === 'recommend' 
    ? (topics || []).slice(0, 6) 
    : topics || [];

  const handleTopicClick = (topicId: string) => {
    navigate(`/community/${topicId}`);
  };
  return <div className="space-y-3 w-full max-w-4xl mx-auto mb-6">

      {/* Category Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {categories.map(category => <button key={category.id} onClick={() => setSelectedCategory(category.id)} className={`px-4 py-2 rounded-2xl text-sm font-medium whitespace-nowrap transition-all ${selectedCategory === category.id ? 'bg-black text-white' : 'bg-transparent border border-[#DADAD9] text-[#858481] hover:bg-gray-50'}`}>
            {category.label}
          </button>)}
      </div>

      {/* Topics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-base font-normal">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-2xl p-4 min-h-[180px]">
              <Skeleton className="h-32 w-full mb-3 rounded-lg" />
              <Skeleton className="h-5 w-3/4 mb-2" />
              <Skeleton className="h-4 w-full" />
            </div>
          ))
        ) : filteredTopics.length > 0 ? (
          filteredTopics.map(topic => (
            <div 
              key={topic.id} 
              onClick={() => handleTopicClick(topic.id)}
              className="bg-white border border-gray-200 rounded-2xl p-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-gray-300 group relative min-h-[180px] flex flex-col justify-between"
            >
              {topic.thumbnail_url && (
                <img 
                  src={topic.thumbnail_url} 
                  alt={topic.title}
                  className="w-full h-32 object-cover rounded-lg mb-3" 
                />
              )}
              <div className="space-y-2 flex-1">
                <h3 className="text-lg font-light text-gray-900 leading-tight">
                  {topic.title}
                </h3>
                {topic.description && (
                  <p className="text-sm text-slate-500 line-clamp-2">
                    {topic.description}
                  </p>
                )}
              </div>
              {topic.tags && topic.tags.length > 0 && (
                <div className="flex gap-1 mt-3 flex-wrap">
                  {topic.tags.slice(0, 3).map((tag, idx) => (
                    <span key={idx} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12 text-slate-500">
            No topics found in this category.
          </div>
        )}
      </div>

      {/* Show more button if there are more topics */}
      {filteredTopics.length > 6 && <div className="text-center">
          <button className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">
            Show more topics
          </button>
        </div>}
    </div>;
};
export default RecommendedTopics;